import { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import { api, API } from "@/lib/api";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Camera, Tag, Dumbbell, Droplets, GlassWater, Pill, Image as ImageIcon, Flame, Beef, Wheat, Droplet, Leaf } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Textarea } from "@/components/ui/textarea";
import MealReviewDialog from "@/components/MealReviewDialog";
import SavedPlanCard from "@/components/SavedPlanCard";
import ResponsiveModal from "@/components/ResponsiveModal";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { usePageRefresh } from "@/lib/refresh";
import VoiceButton from "@/components/VoiceButton";

const todayStr = () => new Date().toISOString().slice(0, 10);

const TREND_LINES = [
  { key: "calories", label: "Calories", color: "#e07a5f", axis: "cal" },
  { key: "protein_g", label: "Protein", color: "#3b82f6", axis: "g" },
  { key: "carbs_g", label: "Carbs", color: "#22c55e", axis: "g" },
  { key: "fat_g", label: "Fat", color: "#a855f7", axis: "g" },
  { key: "fiber_g", label: "Fiber", color: "#14b8a6", axis: "g" },
];

const emptyMeal = { name: "", serving: "", calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0, micros: {}, meal: "snack" };
const emptyWorkout = { name: "", types: [], duration_min: 30, calories_burned: 0, notes: "" };

// Module-level cache so navigating away and back to the Tracker shows data instantly
// (revalidated silently in the background) instead of a blank reload each time.
const trackerCache = {}; // { [date]: { data, trend, workouts, water } }

export default function Tracker() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState({ items: [], summary: { micros: {} }, targets: { calories: 2000, protein_g: 130, carbs_g: 220, fat_g: 65, fiber_g: 30 }, micro_fields: {} });
  const [trend, setTrend] = useState([]);
  const [workouts, setWorkouts] = useState({ items: [], summary: { count: 0, duration_min: 0, calories_burned: 0 }, types: [] });
  const [water, setWater] = useState({ entries: [], total_ml: 0, goal_ml: 2500 });

  // dialogs
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyMeal);
  const [estimating, setEstimating] = useState(false);
  const [reviewTrigger, setReviewTrigger] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showAllMicros, setShowAllMicros] = useState(false);

  const [woOpen, setWoOpen] = useState(false);
  const [woForm, setWoForm] = useState(emptyWorkout);
  const [woEstimating, setWoEstimating] = useState(false);

  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useState("meal");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanNote, setScanNote] = useState("");
  const scanCameraRef = useRef(null);
  const scanGalleryRef = useRef(null);

  const load = async () => {
    const [f, w, wk, wa] = await Promise.all([
      api.get(`/food-logs?date=${date}`),
      api.get(`/food-logs/week?days=14`),
      api.get(`/workouts?date=${date}`),
      api.get(`/water?date=${date}`),
    ]);
    const next = {
      data: f.data,
      trend: w.data.map((d) => ({ ...d, label: d.date.slice(5) })),
      workouts: wk.data,
      water: wa.data,
    };
    trackerCache[date] = next;
    setData(next.data);
    setTrend(next.trend);
    setWorkouts(next.workouts);
    setWater(next.water);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Hydrate instantly from cache (no blank flash), then revalidate silently.
    const c = trackerCache[date];
    if (c) { setData(c.data); setTrend(c.trend); setWorkouts(c.workouts); setWater(c.water); }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
  usePageRefresh(load);

  // ---------- meal ----------
  const macrosFilled = ["calories", "protein_g", "carbs_g", "fat_g", "fiber_g"].some((k) => Number(form[k]) > 0);

  const estimate = async () => {
    if (!form.name.trim()) return toast.warning("Add a food name first");
    setEstimating(true);
    try {
      const { data } = await api.post("/food-logs/estimate", { description: form.name + (form.serving ? `, ${form.serving}` : "") });
      setForm((fm) => ({
        ...fm,
        name: data.name || fm.name,
        serving: data.serving || fm.serving,
        calories: Math.round(data.calories || 0),
        protein_g: Math.round(data.protein_g || 0),
        carbs_g: Math.round(data.carbs_g || 0),
        fat_g: Math.round(data.fat_g || 0),
        fiber_g: Math.round(data.fiber_g || 0),
        micros: data.micros || {},
      }));
      toast.success("Macros & micronutrients estimated");
    } catch (e) {
      toast.error("Estimate failed");
    } finally {
      setEstimating(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.warning("Add a name");
    const { data: created } = await api.post("/food-logs", { ...form, logged_for: date });
    setOpen(false);
    const trigger = {
      log_id: created.id, name: created.name, serving: created.serving, notes: created.notes,
      calories: created.calories, protein_g: created.protein_g, carbs_g: created.carbs_g,
      fat_g: created.fat_g, fiber_g: created.fiber_g,
    };
    setForm(emptyMeal);
    load();
    toast.success("Logged");
    setReviewTrigger(trigger);
  };

  const openEdit = (it) => setEditing({ ...it });
  const saveEdit = async () => {
    if (!editing) return;
    const { id, ...rest } = editing;
    await api.patch(`/food-logs/${id}`, {
      name: rest.name, serving: rest.serving || "",
      calories: Number(rest.calories) || 0, protein_g: Number(rest.protein_g) || 0,
      carbs_g: Number(rest.carbs_g) || 0, fat_g: Number(rest.fat_g) || 0,
      fiber_g: Number(rest.fiber_g) || 0, meal: rest.meal, notes: rest.notes || "",
    });
    setEditing(null);
    load();
    toast.success("Updated");
  };

  const remove = async (id) => { await api.delete(`/food-logs/${id}`); load(); };

  // ---------- workout ----------
  const burnedFilled = Number(woForm.calories_burned) > 0;
  const toggleType = (t) => setWoForm((w) => ({ ...w, types: w.types.includes(t) ? w.types.filter((x) => x !== t) : [...w.types, t] }));

  const estimateWorkout = async () => {
    if (!woForm.name.trim()) return toast.warning("Add a workout name first");
    setWoEstimating(true);
    try {
      const { data } = await api.post("/workouts/estimate", {
        name: woForm.name, types: woForm.types, duration_min: Number(woForm.duration_min) || 0, notes: woForm.notes,
      });
      setWoForm((w) => ({ ...w, calories_burned: Math.round(data.calories_burned || 0), duration_min: Math.round(data.duration_min || w.duration_min) }));
      toast.success("Calories burned estimated");
    } catch (e) {
      toast.error("Estimate failed");
    } finally {
      setWoEstimating(false);
    }
  };

  const saveWorkout = async () => {
    if (!woForm.name.trim()) return toast.warning("Add a workout name");
    await api.post("/workouts", { ...woForm, duration_min: Number(woForm.duration_min) || 0, calories_burned: Number(woForm.calories_burned) || 0, logged_for: date });
    setWoOpen(false);
    setWoForm(emptyWorkout);
    load();
    toast.success("Workout logged");
  };

  const removeWorkout = async (id) => { await api.delete(`/workouts/${id}`); load(); };

  // ---------- water ----------
  const syncWater = async () => {
    try {
      const { data } = await api.get(`/water?date=${date}`);
      setWater(data);
      if (trackerCache[date]) trackerCache[date].water = data;
    } catch (_) {}
  };
  const addWater = async (ml) => {
    // Optimistic: update the UI instantly, then persist + sync in the background.
    setWater((w) => ({ ...w, total_ml: (w.total_ml || 0) + ml, entries: [...(w.entries || []), { id: `tmp-${Date.now()}`, amount_ml: ml }] }));
    try {
      await api.post("/water", { amount_ml: ml, logged_for: date });
      syncWater();
    } catch (_) {
      setWater((w) => ({ ...w, total_ml: Math.max(0, (w.total_ml || 0) - ml) }));
    }
  };
  const undoWater = async () => {
    const last = water.entries[water.entries.length - 1];
    if (!last) return;
    setWater((w) => ({ ...w, total_ml: Math.max(0, (w.total_ml || 0) - (last.amount_ml || 0)), entries: w.entries.slice(0, -1) }));
    try {
      if (!String(last.id).startsWith("tmp-")) await api.delete(`/water/${last.id}`);
      syncWater();
    } catch (_) { syncWater(); }
  };

  // ---------- scan ----------
  const runScan = async (file) => {
    if (!file) return;
    setScanning(true); setScanResult(null);
    try {
      const fd = new FormData();
      fd.append("mode", scanMode);
      fd.append("file", file);
      if (scanNote.trim()) fd.append("note", scanNote.trim());
      const token = localStorage.getItem("kaaya_token");
      const res = await fetch(`${API}/vision/analyze`, {
        method: "POST", credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      setScanResult(d.result);
    } catch (e) {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const acceptScan = async () => {
    if (!scanResult) return;
    const { data: created } = await api.post("/food-logs", {
      name: scanResult.name || "Scanned item", serving: scanResult.serving || "",
      calories: Number(scanResult.calories) || 0, protein_g: Number(scanResult.protein_g) || 0,
      carbs_g: Number(scanResult.carbs_g) || 0, fat_g: Number(scanResult.fat_g) || 0,
      fiber_g: Number(scanResult.fiber_g) || 0, micros: scanResult.micros || {}, meal: "snack", logged_for: date,
      notes: scanResult.note || "",
    });
    setScanOpen(false); setScanResult(null);
    load();
    toast.success("Logged from scan");
    setReviewTrigger({
      log_id: created.id, name: created.name, serving: created.serving, notes: created.notes,
      calories: created.calories, protein_g: created.protein_g, carbs_g: created.carbs_g,
      fat_g: created.fat_g, fiber_g: created.fiber_g,
    });
  };

  const s = data.summary || {};
  const goals = data.targets || { calories: 2000, protein_g: 130, carbs_g: 220, fat_g: 65, fiber_g: 30 };
  const microFields = data.micro_fields || {};
  const microTotals = s.micros || {};
  const microEntries = Object.entries(microFields);
  const waterPct = Math.min(100, Math.round((water.total_ml / (water.goal_ml || 2500)) * 100));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <div className="text-overline">tracker</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-primary tracking-tight">Today's plate</h1>
            <p className="text-foreground/70 mt-1">{new Date(date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full w-44 justify-start font-normal active:scale-95 transition"
                  data-testid="tracker-date-input"
                >
                  <CalendarIcon size={16} className="mr-2 text-primary" />
                  {date === todayStr() ? "Today" : format(parseISO(date), "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={parseISO(date)}
                  onSelect={(d) => d && setDate(format(d, "yyyy-MM-dd"))}
                  disabled={{ after: new Date() }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button variant="outline" className="rounded-full active:scale-95 transition" onClick={() => setScanOpen(true)} data-testid="scan-meal-btn"><Camera size={16} className="mr-1" /> Scan</Button>
            <Button variant="outline" className="rounded-full active:scale-95 transition" onClick={() => setWoOpen(true)} data-testid="add-workout-btn"><Dumbbell size={16} className="mr-1" /> Log workout</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90 active:scale-95 transition" onClick={() => setOpen(true)} data-testid="add-log-btn"><Plus size={16} className="mr-1" /> Log food</Button>
          </div>
        </div>

        {/* Scan modal */}
        <ResponsiveModal open={scanOpen} onOpenChange={(o) => { setScanOpen(o); if (!o) { setScanResult(null); setScanNote(""); } }} title="Scan to log" testid="scan-modal">
          <div className="flex gap-2 mb-2">
            <Button variant={scanMode === "meal" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setScanMode("meal")} data-testid="scan-mode-meal">
              <Camera size={14} className="mr-1" /> Meal photo
            </Button>
            <Button variant={scanMode === "label" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setScanMode("label")} data-testid="scan-mode-label">
              <Tag size={14} className="mr-1" /> Nutrition label
            </Button>
          </div>
          <Textarea
            value={scanNote}
            onChange={(e) => setScanNote(e.target.value)}
            placeholder="Add a note for context — e.g. “my lunch”, “half plate”…"
            className="resize-none min-h-[48px] rounded-2xl bg-surface border-border mb-2"
            data-testid="scan-note-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="rounded-2xl h-auto py-3 flex-col gap-1" onClick={() => scanCameraRef.current?.click()} disabled={scanning} data-testid="scan-camera-btn">
              <Camera size={20} className="text-secondary" />
              <span className="text-sm">{scanning ? "Analyzing…" : "Take a photo"}</span>
            </Button>
            <Button variant="outline" className="rounded-2xl h-auto py-3 flex-col gap-1" onClick={() => scanGalleryRef.current?.click()} disabled={scanning} data-testid="scan-gallery-btn">
              <ImageIcon size={20} className="text-secondary" />
              <span className="text-sm">{scanning ? "Analyzing…" : "Upload from gallery"}</span>
            </Button>
          </div>
          <input ref={scanCameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { runScan(e.target.files?.[0]); e.target.value = ""; }} data-testid="tracker-scan-camera" />
          <input ref={scanGalleryRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { runScan(e.target.files?.[0]); e.target.value = ""; }} data-testid="tracker-scan-file" />
          {scanResult && (
            <div className="mt-3 bg-surface rounded-2xl p-4 border border-border">
              <div className="font-serif-display text-xl text-primary">{scanResult.name}</div>
              <div className="text-xs text-muted-foreground">{scanResult.serving}</div>
              <div className="mt-2 grid grid-cols-5 gap-2 text-center text-sm">
                <div><div className="text-overline">kcal</div>{Math.round(scanResult.calories || 0)}</div>
                <div><div className="text-overline">P</div>{Math.round(scanResult.protein_g || 0)}g</div>
                <div><div className="text-overline">C</div>{Math.round(scanResult.carbs_g || 0)}g</div>
                <div><div className="text-overline">F</div>{Math.round(scanResult.fat_g || 0)}g</div>
                <div><div className="text-overline">Fib</div>{Math.round(scanResult.fiber_g || 0)}g</div>
              </div>
              <Button onClick={acceptScan} className="w-full mt-3 rounded-full bg-primary" data-testid="scan-log-btn">Log it</Button>
            </div>
          )}
        </ResponsiveModal>

        {/* Log workout modal */}
        <ResponsiveModal
          open={woOpen}
          onOpenChange={(o) => { setWoOpen(o); if (!o) setWoForm(emptyWorkout); }}
          title="Log a workout"
          testid="workout-modal"
          footer={
            <>
              {burnedFilled && (
                <Button variant="ghost" onClick={estimateWorkout} disabled={woEstimating} data-testid="workout-reestimate-btn" className="rounded-full">
                  <Sparkles size={16} className="mr-1" /> Re-estimate
                </Button>
              )}
              {burnedFilled ? (
                <Button onClick={saveWorkout} data-testid="workout-save-btn" className="rounded-full bg-primary hover:bg-primary/90">Save</Button>
              ) : (
                <Button onClick={estimateWorkout} disabled={woEstimating} data-testid="workout-estimate-btn" className="rounded-full bg-primary hover:bg-primary/90">
                  <Sparkles size={16} className="mr-1" /> {woEstimating ? "Estimating…" : "AI estimate"}
                </Button>
              )}
            </>
          }
        >
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="workout-name" value={woForm.name} onChange={(e) => setWoForm({ ...woForm, name: e.target.value })} placeholder="Morning run, leg day…" />
            </div>
            <div>
              <Label>Type</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {(workouts.types || []).map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm capitalize cursor-pointer" data-testid={`workout-type-${t}`}>
                    <Checkbox checked={woForm.types.includes(t)} onCheckedChange={() => toggleType(t)} />
                    {t}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duration (min)</Label><Input type="number" data-testid="workout-duration" value={woForm.duration_min} onChange={(e) => setWoForm({ ...woForm, duration_min: Number(e.target.value) })} /></div>
              <div><Label>Calories burned</Label><Input type="number" data-testid="workout-calories" value={woForm.calories_burned} onChange={(e) => setWoForm({ ...woForm, calories_burned: Number(e.target.value) })} /></div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={woForm.notes} onChange={(e) => setWoForm({ ...woForm, notes: e.target.value })} placeholder="intensity, distance, sets…" data-testid="workout-notes" />
            </div>
          </div>
        </ResponsiveModal>

        {/* Log food modal */}
        <ResponsiveModal
          open={open}
          onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyMeal); }}
          title="Log a meal or snack"
          testid="food-modal"
          footer={
            <>
              {macrosFilled && (
                <Button variant="ghost" onClick={estimate} disabled={estimating} data-testid="reestimate-btn" className="rounded-full">
                  <Sparkles size={16} className="mr-1" /> Re-estimate
                </Button>
              )}
              {macrosFilled ? (
                <Button onClick={save} data-testid="save-log-btn" className="rounded-full bg-primary hover:bg-primary/90">Save</Button>
              ) : (
                <Button onClick={estimate} disabled={estimating} data-testid="estimate-btn" className="rounded-full bg-primary hover:bg-primary/90">
                  <Sparkles size={16} className="mr-1" /> {estimating ? "Estimating…" : "AI estimate"}
                </Button>
              )}
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <div className="flex items-center gap-2">
                <Input data-testid="log-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Grilled salmon with quinoa" />
                <VoiceButton
                  testId="log-name-voice"
                  onTranscript={(t) => setForm((f) => ({ ...f, name: f.name ? f.name.trim() + " " + t : t }))}
                />
              </div>
            </div>
            <div>
              <Label>Serving</Label>
              <Input data-testid="log-serving" value={form.serving} onChange={(e) => setForm({ ...form, serving: e.target.value })} placeholder="1 bowl / 200g" />
            </div>
            <div>
              <Label>Meal</Label>
              <Select value={form.meal} onValueChange={(v) => setForm({ ...form, meal: v })}>
                <SelectTrigger data-testid="log-meal-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="dinner">Dinner</SelectItem>
                  <SelectItem value="snack">Snack</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Calories</Label><Input type="number" data-testid="log-cal" value={form.calories} onChange={(e) => setForm({ ...form, calories: Number(e.target.value) })} /></div>
            <div><Label>Protein (g)</Label><Input type="number" data-testid="log-protein" value={form.protein_g} onChange={(e) => setForm({ ...form, protein_g: Number(e.target.value) })} /></div>
            <div><Label>Carbs (g)</Label><Input type="number" data-testid="log-carbs" value={form.carbs_g} onChange={(e) => setForm({ ...form, carbs_g: Number(e.target.value) })} /></div>
            <div><Label>Fat (g)</Label><Input type="number" data-testid="log-fat" value={form.fat_g} onChange={(e) => setForm({ ...form, fat_g: Number(e.target.value) })} /></div>
            <div><Label>Fiber (g)</Label><Input type="number" data-testid="log-fiber" value={form.fiber_g} onChange={(e) => setForm({ ...form, fiber_g: Number(e.target.value) })} /></div>
          </div>
          {Object.keys(form.micros || {}).length > 0 && (
            <div className="text-xs text-muted-foreground bg-surface rounded-xl p-2 mt-2" data-testid="log-micros-preview">
              Micros: {Object.entries(form.micros).map(([k, v]) => `${microFields[k]?.label || k} ${v}${microFields[k]?.unit || ""}`).join(" · ")}
            </div>
          )}
        </ResponsiveModal>

        {/* Calorie + workout hero */}
        <CalorieHero intake={s.calories || 0} burned={workouts.summary?.calories_burned || 0} goal={goals.calories} workoutMin={workouts.summary?.duration_min || 0} workoutCount={workouts.summary?.count || 0} />
        <SavedPlanCard onLogged={load} />

        {/* Macro tiles — all 4 in one row */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-4">
          <MacroCard icon={Beef} label="Protein" value={s.protein_g || 0} goal={goals.protein_g} unit="g" color="#3b82f6" testid="summary-protein" />
          <MacroCard icon={Wheat} label="Carbs" value={s.carbs_g || 0} goal={goals.carbs_g} unit="g" color="#22c55e" testid="summary-carbs" />
          <MacroCard icon={Droplet} label="Fat" value={s.fat_g || 0} goal={goals.fat_g} unit="g" color="#a855f7" testid="summary-fat" />
          <MacroCard icon={Leaf} label="Fiber" value={s.fiber_g || 0} goal={goals.fiber_g} unit="g" color="#14b8a6" testid="summary-fiber" />
        </div>

        {/* Hydration */}
        <div className="bg-surface-elevated rounded-3xl border border-border p-5 mb-8" data-testid="water-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-overline" style={{ color: "#3b82f6" }}><Droplets size={15} /> hydration</div>
            <button onClick={undoWater} className="text-xs text-muted-foreground hover:text-primary" data-testid="water-undo-btn">undo</button>
          </div>
          <div className="flex items-end justify-between gap-4 mt-2">
            <div className="flex items-baseline gap-1">
              <span className="font-serif-display text-4xl text-primary tabular-nums" data-testid="water-total">{Math.round(water.total_ml)}</span>
              <span className="text-sm text-muted-foreground">/ {water.goal_ml} ml</span>
            </div>
            <div className="flex items-center gap-1.5" data-testid="water-glasses">
              {Array.from({ length: 8 }).map((_, i) => {
                const filled = water.total_ml >= (i + 1) * (water.goal_ml / 8);
                return <GlassWater key={i} size={18} className="transition-colors duration-500" style={{ color: filled ? "#3b82f6" : "hsl(var(--muted-foreground) / 0.3)" }} />;
              })}
            </div>
          </div>
          <div className="mt-3 h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${waterPct}%`, background: "linear-gradient(90deg,#60a5fa,#3b82f6)" }} />
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" className="rounded-full flex-1 active:scale-95 transition" onClick={() => addWater(250)} data-testid="water-add-glass"><GlassWater size={14} className="mr-1" /> +1 glass</Button>
            <Button size="sm" variant="outline" className="rounded-full flex-1 active:scale-95 transition" onClick={() => addWater(500)} data-testid="water-add-500"><Droplet size={14} className="mr-1" /> +500ml</Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Food + workout entries */}
          <div className="lg:col-span-2 min-w-0 space-y-6">
            <div className="bg-surface-elevated rounded-3xl border border-border p-6">
              <div className="text-overline mb-4">today's meals</div>
              {(!data.items || data.items.length === 0) && (
                <div className="text-foreground/60 text-sm py-8 text-center">No entries yet. Log from chat or click "Log food" above.</div>
              )}
              <div className="divide-y divide-border max-h-[60vh] overflow-y-auto overscroll-contain scrollbar-thin pr-1" data-testid="log-list">
                {data.items?.map((it) => (
                  <div key={it.id} className="py-3 flex items-center justify-between gap-3 hover:bg-surface/50 cursor-pointer rounded-xl px-2 -mx-2" onClick={() => openEdit(it)} data-testid={`log-row-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-overline text-secondary">{it.meal}</div>
                      <div className="font-serif-display text-xl text-primary truncate">{it.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{it.serving}{it.notes ? ` • ${it.notes}` : ""}</div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-foreground/90">{Math.round(it.calories)} kcal</div>
                      <div className="text-muted-foreground">P {Math.round(it.protein_g)} • C {Math.round(it.carbs_g)} • F {Math.round(it.fat_g)} • Fib {Math.round(it.fiber_g || 0)}</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(it.id); }} data-testid={`del-log-${it.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-surface-elevated rounded-3xl border border-border p-6">
              <div className="flex items-center gap-2 text-overline mb-4"><Dumbbell size={14} /> today's workouts</div>
              {(!workouts.items || workouts.items.length === 0) && (
                <div className="text-foreground/60 text-sm py-6 text-center">No workouts logged. Click "Log workout" or tell the chat what you did.</div>
              )}
              <div className="divide-y divide-border max-h-[40vh] overflow-y-auto overscroll-contain scrollbar-thin pr-1" data-testid="workout-list">
                {workouts.items?.map((w) => (
                  <div key={w.id} className="py-3 flex items-center justify-between gap-3" data-testid={`workout-row-${w.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display text-xl text-primary truncate">{w.name}</div>
                      <div className="text-xs text-muted-foreground truncate capitalize">{(w.types || []).join(", ") || "workout"}{w.notes ? ` • ${w.notes}` : ""}</div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-foreground/90">{Math.round(w.calories_burned)} kcal</div>
                      <div className="text-muted-foreground">{Math.round(w.duration_min)} min</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeWorkout(w.id)} data-testid={`del-workout-${w.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Micronutrients */}
          <div className="bg-surface-elevated rounded-3xl border border-border p-6 min-w-0 overflow-hidden" data-testid="micronutrient-panel">
            <div className="flex items-center gap-2 text-overline mb-1"><Pill size={14} /> micronutrients</div>
            <div className="font-serif-display text-2xl text-primary mb-4">Vitamins & minerals</div>
            <div className="space-y-3.5">
              {(showAllMicros ? microEntries : microEntries.slice(0, 5)).map(([key, meta]) => {
                const val = microTotals[key] || 0;
                const pct = Math.min(100, Math.round((val / (meta.rda || 1)) * 100));
                const over = (meta.rda || 0) > 0 && val > meta.rda;
                return (
                  <div key={key} data-testid={`micro-${key}`} className="min-w-0">
                    <div className="flex items-baseline justify-between gap-2 min-w-0 text-sm mb-1">
                      <span className="text-foreground/80 flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{meta.label}</span>
                        {meta.custom && (
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-secondary bg-secondary/10 rounded-full px-1.5 py-0.5" title="Target personalized for your profile" data-testid={`micro-custom-${key}`}>for you</span>
                        )}
                      </span>
                      <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground tabular-nums">{val}{meta.unit}<span className="opacity-60"> / {meta.rda}{meta.unit}</span></span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full max-w-full transition-all duration-500 ${over ? "bg-primary" : meta.custom ? "bg-secondary" : "bg-secondary/60"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {microEntries.length > 5 && (
              <button onClick={() => setShowAllMicros((v) => !v)} className="mt-4 text-sm font-medium text-secondary hover:underline active:scale-95 transition" data-testid="micro-view-all-toggle">
                {showAllMicros ? "Show less" : `View all ${microEntries.length}`}
              </button>
            )}
            <p className="text-xs text-muted-foreground mt-4">Logged automatically when the AI estimates a meal, or via chat. Targets marked <span className="text-secondary font-medium">"for you"</span> are personalized to your health profile by your coach; the rest use general adult guidelines.</p>
          </div>
        </div>

        {/* 14-day trend */}
        <div className="bg-surface-elevated rounded-3xl border border-border p-6">
          <div className="text-overline mb-1">rolling 2 weeks</div>
          <div className="font-serif-display text-2xl text-primary mb-4">Nutrition trend</div>
          <div className="h-36 sm:h-40" style={{ minHeight: 144 }} data-testid="trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 21% 85%)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(40 7% 45%)" />
                <YAxis yAxisId="g" tick={{ fontSize: 11 }} stroke="hsl(40 7% 45%)" />
                <YAxis yAxisId="cal" orientation="right" tick={{ fontSize: 11 }} stroke="#e07a5f" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {TREND_LINES.map((l) => (
                  <Line key={l.key} yAxisId={l.axis} type="monotone" dataKey={l.key} name={l.label} stroke={l.color} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Calories use the right axis (kcal); protein, carbs, fat and fiber use the left axis (g).</p>
        </div>
      </div>

      <MealReviewDialog trigger={reviewTrigger} onClose={() => setReviewTrigger(null)} onApplied={() => load()} />

      <ResponsiveModal
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        title="Edit meal"
        testid="edit-meal-modal"
        footer={<Button onClick={saveEdit} className="rounded-full bg-primary" data-testid="edit-log-save-btn">Save changes</Button>}
      >
        {editing && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label>
              <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-log-name" />
            </div>
            <div><Label>Serving</Label>
              <Input value={editing.serving || ""} onChange={(e) => setEditing({ ...editing, serving: e.target.value })} data-testid="edit-log-serving" />
            </div>
            <div><Label>Meal</Label>
              <Select value={editing.meal} onValueChange={(v) => setEditing({ ...editing, meal: v })}>
                <SelectTrigger data-testid="edit-log-meal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="dinner">Dinner</SelectItem>
                  <SelectItem value="snack">Snack</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Calories</Label><Input type="number" value={editing.calories} onChange={(e) => setEditing({ ...editing, calories: Number(e.target.value) })} data-testid="edit-log-cal" /></div>
            <div><Label>Protein (g)</Label><Input type="number" value={editing.protein_g} onChange={(e) => setEditing({ ...editing, protein_g: Number(e.target.value) })} data-testid="edit-log-protein" /></div>
            <div><Label>Carbs (g)</Label><Input type="number" value={editing.carbs_g} onChange={(e) => setEditing({ ...editing, carbs_g: Number(e.target.value) })} data-testid="edit-log-carbs" /></div>
            <div><Label>Fat (g)</Label><Input type="number" value={editing.fat_g} onChange={(e) => setEditing({ ...editing, fat_g: Number(e.target.value) })} data-testid="edit-log-fat" /></div>
            <div><Label>Fiber (g)</Label><Input type="number" value={editing.fiber_g || 0} onChange={(e) => setEditing({ ...editing, fiber_g: Number(e.target.value) })} data-testid="edit-log-fiber" /></div>
            <div className="col-span-2"><Label>Instructions / Notes</Label>
              <Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="e.g., ate after gym, no oil added, post-workout shake…" data-testid="edit-log-notes" />
            </div>
          </div>
        )}
      </ResponsiveModal>
    </Layout>
  );
}

function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => { const id = requestAnimationFrame(() => setM(true)); return () => cancelAnimationFrame(id); }, []);
  return m;
}

function useCountUp(value, dur = 900) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; const to = Number(value) || 0; const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, dur]);
  return n;
}

function ProgressRing({ pct, size = 168, stroke = 14, color, children }) {
  const mounted = useMounted();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.min(100, Math.max(0, pct));
  const offset = c - (mounted ? clamped : 0) / 100 * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(0.22,1,0.36,1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
}

function CalorieHero({ intake, burned, goal, workoutMin, workoutCount }) {
  const pct = Math.round((intake / (goal || 1)) * 100);
  const display = useCountUp(intake);
  const burnedDisplay = useCountUp(burned);
  const remaining = Math.round((goal || 0) - intake);
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
      {/* Calories */}
      <div className="bg-surface-elevated rounded-3xl border border-border p-4 sm:p-5 flex flex-col items-center justify-center" data-testid="summary-calories">
        <ProgressRing pct={pct} color="#e07a5f" size={124} stroke={11}>
          <Flame size={18} className="text-secondary mb-0.5" />
          <span className="font-serif-display text-3xl text-primary leading-none tabular-nums" data-testid="tracker-kcal-hero-value">{display}</span>
          <span className="text-[11px] text-muted-foreground mt-0.5">/ {Math.round(goal)}</span>
          <span className="text-[11px] font-medium mt-0.5" style={{ color: remaining >= 0 ? "#3A4D39" : "#E27D60" }}>
            {remaining >= 0 ? `${remaining} left` : `${Math.abs(remaining)} over`}
          </span>
        </ProgressRing>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mt-2">Calories</div>
      </div>
      {/* Workout */}
      <div className="bg-surface-elevated rounded-3xl border border-border p-4 sm:p-5 flex flex-col items-center justify-center text-center" data-testid="workout-summary-card">
        <span className="grid place-items-center h-12 w-12 rounded-full mb-2" style={{ backgroundColor: "#FF8C421A" }}>
          <Dumbbell size={22} style={{ color: "#FF8C42" }} />
        </span>
        <span className="font-serif-display text-4xl text-primary leading-none tabular-nums" data-testid="workout-summary-val">{burnedDisplay}</span>
        <span className="text-[11px] text-muted-foreground mt-1">kcal burned</span>
        <span className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">{Math.round(workoutMin)} min · {workoutCount} workout{workoutCount === 1 ? "" : "s"}</span>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mt-2">Workout</div>
      </div>
    </div>
  );
}

// (HeroStat removed — the calorie hero now shows kcal + workout tiles side by side.)

function MacroCard({ icon: Icon, label, value, goal, unit, color, testid }) {
  const mounted = useMounted();
  const display = useCountUp(value);
  const pct = Math.min(100, Math.round((value / (goal || 1)) * 100));
  return (
    <div className="bg-surface-elevated rounded-2xl border border-border p-2.5 sm:p-3 min-w-0 overflow-hidden" data-testid={testid}>
      <span className="flex items-center gap-1 min-w-0" style={{ color }}>
        <Icon size={12} className="shrink-0" />
        <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide truncate">{label}</span>
      </span>
      <div className="mt-1.5 flex items-baseline gap-0.5 min-w-0">
        <span className="font-serif-display text-xl sm:text-2xl text-primary leading-none tabular-nums">{display}</span>
        <span className="text-[10px] text-muted-foreground truncate">/{Math.round(goal)}{unit}</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full max-w-full" style={{ width: mounted ? `${pct}%` : 0, backgroundColor: color, transition: "width 1.1s cubic-bezier(0.22,1,0.36,1)" }} data-testid={`tracker-macro-${label.toLowerCase()}-bar`} />
      </div>
    </div>
  );
}
